import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ConfirmTypes, GenericDialogsService, SaveChangesResult } from '@siza-staff-care/shared/generic-dialogs';
import { typedClone } from '@siza-staff-care/shared/utils';
import { map, Observable, of, tap, withLatestFrom } from 'rxjs';
import { GlobalStoreRegistryStore } from './global-store-registry';
import { IStore } from './store-interface';

export interface UnsavedChangesMessageOverride {
	title: string;
	message: string;
}

interface StoreRegistryState {
	storeHierarchy: StoreHierarchyMap;
	stores: IStore[];
	activeStore?: IStore;
	unsavedChangesMessageOverride?: UnsavedChangesMessageOverride;
	forceHasUnsavedChanges?: boolean;
}

interface StoreEntry {
	store: IStore;
	parentStore?: IStore;
	children?: StoreEntry[];
}

type StoreHierarchyMap = { [niceName: string]: StoreEntry };

const defaultState: StoreRegistryState = {
	storeHierarchy: {},
	stores: [],
};

const DEBUG = false;

@Injectable()
export class StoreRegistry extends ComponentStore<StoreRegistryState> {
	readonly activeStore$ = this.select(({ activeStore }) => activeStore);
	readonly stores$ = this.select(({ stores }) => stores);
	readonly storeHierarchy$ = this.select(({ storeHierarchy }) => storeHierarchy);

	private readonly updateSetActiveStore = this.updater((state, activeStore: IStore | undefined) => {
		if (DEBUG && activeStore)
			console.log(
				`%c updateActiveStore ${activeStore.niceName} `,
				'color: white; background-color: orange; font-size: 1rem;',
			);

		return {
			...state,
			activeStore,
		};
	});

	readonly registerStore = this.effect((store$: Observable<{ parentStore?: IStore; store: IStore }>) => {
		return store$.pipe(
			withLatestFrom(this.stores$, this.storeHierarchy$),
			tap(([{ parentStore, store }, stores, storeHierarchy]) => {
				if (stores.indexOf(store) !== -1) {
					throw `Store ${store.niceName} is already registered.`;
				}

				let parentEntry: StoreEntry | undefined;
				if (parentStore) {
					parentEntry = storeHierarchy[parentStore.niceName];
					if (!parentEntry) {
						throw `Parent store ${parentStore.niceName} is not registered.`;
					}
				}

				const storeEntry: StoreEntry = { store, parentStore };
				if (parentEntry) {
					parentEntry.children = parentEntry.children || [];
					parentEntry.children.push(storeEntry);
				}

				stores.push(store);
				storeHierarchy[store.niceName] = storeEntry;
			}),
		);
	});

	readonly setActiveStore = this.effect((activeStore$: Observable<IStore | undefined>) =>
		activeStore$.pipe(
			tap((activeStore) => {
				if (DEBUG) {
					console.log(
						`%c setActiveStore ${activeStore?.niceName || '<NONE>'} `,
						'color: white; background-color: blue; font-size: 1rem;',
					);
				}

				if (activeStore && typeof activeStore.registryActivate === 'function') {
					activeStore.registryActivate();
				}

				this.updateSetActiveStore(activeStore);
			}),
		),
	);

	/**
	 * Discards all changes across all registered stores.
	 */
	readonly discardAllChanges = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				const stores = this.getAllStores();
				stores.forEach((s) => s.setHasUnsavedChanges(false));
			}),
		),
	);

	readonly registerUnsavedChangesOverride = this.effect(
		(override$: Observable<UnsavedChangesMessageOverride | undefined>) =>
			override$.pipe(
				tap((override) => {
					this.patchState({ unsavedChangesMessageOverride: override });

					// Ensure that the flag for unsaved changes is cleared if there's no override message.
					if (!override) {
						this.setForceHasUnsavedChanges(false);
					}
				}),
			),
	);

	readonly setForceHasUnsavedChanges = this.effect((forceHasUnsavedChanges$: Observable<boolean>) =>
		forceHasUnsavedChanges$.pipe(
			tap((forceHasUnsavedChanges) => {
				this.patchState({ forceHasUnsavedChanges });

				// Register forced unsaved changes at a global level (used by the window:beforeunload event to warn the user)
				this.globalRegistry.setForceHasUnsavedChanges(forceHasUnsavedChanges);
			}),
		),
	);

	/**
	 * Returns whether there are unsaved changes at the registry level.
	 */
	get hasUnsavedChanges() {
		// If there's an unsaved changes message override, always return true.
		// This is used for workflows. If this exists then the override message for closing without saving must be used.
		const { unsavedChangesMessageOverride: unsavedChangesMessageOverride, forceHasUnsavedChanges } = this.get();
		return !!(unsavedChangesMessageOverride && forceHasUnsavedChanges);
	}

	get hasUnsavedChangesMessageOverride() {
		const { unsavedChangesMessageOverride } = this.get();
		return !!unsavedChangesMessageOverride;
	}

	constructor(
		private readonly genericDialogs: GenericDialogsService,
		private globalRegistry: GlobalStoreRegistryStore,
	) {
		super(typedClone(defaultState));
	}

	showUnsavedChangesMessageOverride(): Observable<SaveChangesResult | undefined> {
		const { unsavedChangesMessageOverride: unsavedChangesMessageOverride } = this.get();
		if (unsavedChangesMessageOverride) {
			return this.showUnsavedChangesMessage(unsavedChangesMessageOverride);
		}

		return of(undefined);
	}

	private showUnsavedChangesMessage(
		override: UnsavedChangesMessageOverride,
	): Observable<SaveChangesResult | undefined> {
		return this.genericDialogs
			.confirm({
				message: override.message,
				title: override.title,
				type: ConfirmTypes.okCancel,
				customConfirmCaption: 'Discard Changes',
			})
			.pipe(
				map((result) => {
					return result ? SaveChangesResult.Discard : SaveChangesResult.Cancel;
				}),
			);
	}

	/**
	 * Register multiple independent stores at once.
	 * @param stores
	 */
	registerStores(...stores: IStore[]) {
		stores.forEach((store) => this.registerStore({ store: store }));
	}

	registerStoresWithParent(parentStore: IStore, childStores: IStore[]) {
		childStores.forEach((store) => this.registerStore({ parentStore, store }));
	}

	/**
	 * Gets the child stores of the given store.
	 * @param store
	 * @returns
	 */
	getChildStores(store: IStore) {
		const { storeHierarchy } = this.get();
		const entry = storeHierarchy[store.niceName];
		return entry?.children || [];
	}

	/**
	 * Gets the child stores of the given store recursively.
	 * @param store
	 * @returns
	 */
	getChildStoresRecursive(store: IStore) {
		const { storeHierarchy } = this.get();
		const entry = storeHierarchy[store.niceName];
		const children = entry?.children || [];
		return (
			children.reduce((acc, child) => {
				acc.push(child.store);
				acc.push(...this.getChildStoresRecursive(child.store));
				return acc;
			}, [] as IStore[]) || []
		);
	}

	/**
	 * Gets all the stores registered.
	 * @returns
	 */
	getAllStores() {
		const { stores } = this.get();
		return stores;
	}

	/**
	 * Gets the currently active store.
	 * @returns
	 */
	getActiveStore() {
		const { activeStore } = this.get();
		return activeStore;
	}

	/**
	 * Gets the store with no parent.
	 * @returns
	 */
	getRootStore() {
		const { stores } = this.get();
		return stores.find((s) => !this.getParentStore(s));
	}

	/**
	 * Gets the parent store of a given store.
	 * @param store
	 * @returns
	 */
	getParentStore(store: IStore) {
		const { storeHierarchy } = this.get();
		const entry = storeHierarchy[store.niceName];
		return entry?.parentStore;
	}

	/**
	 * Gets a store by its key.
	 * @param key
	 * @returns
	 */
	getStoreByKey(key: string) {
		const { stores } = this.get();
		return stores.find((s) => s.storeKey === key);
	}
}
